/**
 * Kiosk-only paging when a factory has more floor cards than fit clearly on a wall.
 *
 * Desk mode always shows every floor (scroll is fine). Kiosk mode shows {@link KIOSK_PAGE_SIZE}
 * panels at a time and advances on a timer so BCA-sized factories stay readable without scrolling.
 * Presentation only — the full floors result is still fetched; this slices what is painted (§4.3
 * reshape of a complete list, not a second query).
 */

import { useEffect, useMemo, useState } from 'react';

/** Max floor cards on screen at once in kiosk — 2×3 or 3×2 depending on density tier. */
export const KIOSK_PAGE_SIZE = 6;

/** How long each page stays up before the next set of floors. */
export const KIOSK_PAGE_MS = 12_000;

export interface KioskFloorPages<T> {
  /** Floors to paint this cycle (all of them when not kiosk or ≤ page size). */
  visible: T[];
  /** 0-based page index. */
  page: number;
  /** Total pages (≥ 1). */
  pageCount: number;
  /** True when kiosk is rotating through more than one page. */
  rotating: boolean;
  /** "1–6 of 13" style label for the kiosk chrome, or null when not paging. */
  rangeLabel: string | null;
}

export function useKioskFloorPages<T>(floors: readonly T[], isKiosk: boolean): KioskFloorPages<T> {
  const pageCount =
    isKiosk && floors.length > KIOSK_PAGE_SIZE
      ? Math.ceil(floors.length / KIOSK_PAGE_SIZE)
      : 1;
  const rotating = pageCount > 1;

  const [page, setPage] = useState(0);
  const [prevFloors, setPrevFloors] = useState(floors);
  const [prevIsKiosk, setPrevIsKiosk] = useState(isKiosk);

  // New result or leaving kiosk: start at the first page so a filter change does not open mid-cycle.
  // Adjust during render (React's "store info from previous renders" pattern) — not in an effect,
  // which would cascade a second paint and trips react-hooks/set-state-in-effect.
  if (floors !== prevFloors || isKiosk !== prevIsKiosk) {
    setPrevFloors(floors);
    setPrevIsKiosk(isKiosk);
    setPage(0);
  }

  useEffect(() => {
    if (!rotating) {
      return;
    }
    const id = window.setInterval(() => {
      setPage((current) => (current + 1) % pageCount);
    }, KIOSK_PAGE_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [rotating, pageCount]);

  // Derive a safe index rather than clamping in an effect (pageCount falls with the floors list).
  const safePage = rotating ? (page >= pageCount ? 0 : page) : 0;

  const visible = useMemo(() => {
    if (!rotating) {
      return [...floors];
    }
    const start = safePage * KIOSK_PAGE_SIZE;
    return floors.slice(start, start + KIOSK_PAGE_SIZE);
  }, [floors, rotating, safePage]);

  const rangeLabel = useMemo(() => {
    if (!rotating || floors.length === 0) {
      return null;
    }
    const start = safePage * KIOSK_PAGE_SIZE + 1;
    const end = Math.min((safePage + 1) * KIOSK_PAGE_SIZE, floors.length);
    return `${String(start)}–${String(end)} of ${String(floors.length)}`;
  }, [rotating, floors.length, safePage]);

  return {
    visible,
    page: safePage,
    pageCount,
    rotating,
    rangeLabel,
  };
}
